// 推荐功能页面组件
// 用户可以查看推荐链接、推荐统计和奖励历史

import React, { useState, useEffect } from 'react'
import { Copy, Layers, ArrowRightLeft, Crown, HelpCircle, Settings, LogOut } from 'lucide-react'
import { api } from '../lib/api'
import Pagination from '../components/Pagination'
import LoadingSpinner from '../components/LoadingSpinner'
import ConfirmDialog from '../components/ConfirmDialog'
import SuccessModal from '../components/SuccessModal';
import LanguageSelector from '../components/LanguageSelector'
import { useTranslation } from 'react-i18next'

interface ReferredUser {
    id: string
    email: string
    registered_at: string
    total_spent: number
    payment_count: number
}

// 定义类型接口
interface ReferralConfig {
    registration_reward: number
    recharge_percentage: number
    max_reward_per_recharge: number
    max_referrals: number
    first_charge_bonus: number
}

// 提现相关类型定义
interface WithdrawalRequest {
    amount: number
    password: string
    withdrawal_method: 'paypal'
    paypal_email: string
}

interface WithdrawalRecord {
    id: number
    amount: number
    method: 'paypal'
    accountInfo: string
    status: 'pending' | 'processing' | 'completed' | 'rejected'
    note?: string
    rejectionReason?: string
    createdAt: string
    processedAt?: string
    fee: number
    actualAmount: number
}

interface ReferralStats {
    this_month_count: number
    last_30_days_count: number
    latest_referral_time: string | null
    conversion_rate: number
}

interface ReferralInfo {
    referral_code: string
    referral_link: string
    referral_count: number
    total_earnings: number
    registration_rewards: number
    recharge_rewards: number
    is_referred_user: boolean
    referral_stats: ReferralStats
    config: ReferralConfig
    user_level?: number
    level_info?: {
        current_level: number
        level_name: string
        reward_percentage: number
        current_earnings: number
        required_earnings: number
        next_level_required: number | null
        progress_to_next: number
    }
}

interface RewardHistory {
    id: string
    reward_type: 'registration' | 'recharge'
    reward_amount: number
    recharge_amount: number
    created_at: string
    referred_email: string
}


const Referrals: React.FC = () => {
    const { t } = useTranslation()
    const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null)
    const [rewardHistory, setRewardHistory] = useState<RewardHistory[]>([])
    const [availableBalance, setAvailableBalance] = useState<number>(0)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [copied, setCopied] = useState(false)

    // 设置页面标题
    useEffect(() => {
        document.title = `${t('referrals_title')} - ModelPool`
    }, [t])
    const [showUsersModal, setShowUsersModal] = useState(false)
    const [modalUsers, setModalUsers] = useState<ReferredUser[]>([])
    const [modalLoading, setModalLoading] = useState(false)
    const [modalPage, setModalPage] = useState(1)
    const [modalTotalPages, setModalTotalPages] = useState(0)
    const [modalTotalRecords, setModalTotalRecords] = useState(0)
    const [modalPageSize, setModalPageSize] = useState(10)
    // 新增：奖励弹窗相关状态
    const [showRewardsModal, setShowRewardsModal] = useState(false)
    const [modalRewards, setModalRewards] = useState<RewardHistory[]>([])
    const [rewardsModalLoading, setRewardsModalLoading] = useState(false)
    const [rewardsModalPage, setRewardsModalPage] = useState(1)
    const [rewardsModalTotalPages, setRewardsModalTotalPages] = useState(0)
    const [rewardsModalTotalRecords, setRewardsModalTotalRecords] = useState(0)
    const [rewardsModalPageSize, setRewardsModalPageSize] = useState(10)

    // 提现相关状态
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false)
    const [showWithdrawalRecordsModal, setShowWithdrawalRecordsModal] = useState(false)
    const [withdrawalRecords, setWithdrawalRecords] = useState<WithdrawalRecord[]>([])
    const [withdrawalLoading, setWithdrawalLoading] = useState(false)
    const [withdrawalForm, setWithdrawalForm] = useState<WithdrawalRequest>({
        amount: 0,
        password: '',
        withdrawal_method: 'paypal',
        paypal_email: ''
    })
    const [withdrawalError, setWithdrawalError] = useState('')
    const [withdrawalSuccess, setWithdrawalSuccess] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // 转换功能相关状态
    const [showConvertModal, setShowConvertModal] = useState(false)
    const [showConvertConfirmModal, setShowConvertConfirmModal] = useState(false)
    const [showConvertSuccessModal, setShowConvertSuccessModal] = useState(false)
    const [convertAmount, setConvertAmount] = useState(0)
    const [convertLoading, setConvertLoading] = useState(false)
    const [convertError, setConvertError] = useState('')
    const [convertSuccess, setConvertSuccess] = useState('')

    // 用户菜单相关状态
    const [showUserMenu, setShowUserMenu] = useState(false)

    // 获取头像颜色的函数

    // 处理登出
    const handleLogout = () => {
        localStorage.removeItem('apiKey')
        window.location.href = '/login'
    }

    // 点击外部关闭用户菜单
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element
            if (!target.closest('.user-menu-container')) {
                setShowUserMenu(false)
            }
        }

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showUserMenu])

    // 获取推荐信息
    const fetchReferralInfo = async () => {
        try {
            const response = await api.get('/api/referral/info')
            setReferralInfo(response.data.data)
        } catch (err) {
            console.error('Failed to fetch referral info:', err)
            setError(t('failedToLoadReferralInfo'))
        }
    }

    // 获取奖励余额信息
    const fetchRewardBalance = async () => {
        try {
            const response = await api.get('/api/reward-balance/info')
            // 更新referralInfo中的total_earnings字段
            if (referralInfo && response.data.data) {
                setReferralInfo({
                    ...referralInfo,
                    total_earnings: response.data.data.reward_balance || 0
                })
            }
        } catch (err) {
            console.error('Failed to fetch reward balance:', err)
        }
    }

    // 获取可用余额信息
    const fetchAvailableBalance = async () => {
        try {
            const response = await api.get('/api/reward-balance/info')
            if (response.data.data) {
                setAvailableBalance(response.data.data.available_balance || 0)
            }
        } catch (err) {
            console.error('Failed to fetch available balance:', err)
        }
    }

    // 获取奖励历史
    const fetchRewardHistory = async () => {
        try {
            const response = await api.get('/api/referral/rewards?limit=10')
            setRewardHistory(response.data.data || [])
        } catch (err) {
            console.error('Failed to fetch reward history:', err)
        }
    }

    // 获取推荐用户列表（用于弹窗）
    const fetchReferredUsersForModal = async (page = 1, limit = 10) => {
        try {
            setModalLoading(true)
            const response = await api.get(`/api/referral/users?limit=${limit}&offset=${(page - 1) * limit}`)
            const data = response.data
            
            setModalUsers(data.data || [])
            setModalTotalRecords(data.total || 0)
            setModalTotalPages(Math.ceil((data.total || 0) / limit))
            setModalPage(page)
        } catch (err) {
            console.error('Failed to fetch referred users:', err)
        } finally {
            setModalLoading(false)
        }
    }

    // 获取奖励历史数据（用于弹窗）
    const fetchRewardsForModal = async (page = 1, limit = 10) => {
        try {
            setRewardsModalLoading(true)
            const response = await api.get(`/api/referral/rewards?limit=${limit}&offset=${(page - 1) * limit}`)
            const data = response.data
            
            setModalRewards(data.data || [])
            setRewardsModalTotalRecords(data.total || 0)
            setRewardsModalTotalPages(Math.ceil((data.total || 0) / limit))
            setRewardsModalPage(page)
        } catch (err) {
            console.error('Failed to fetch rewards for modal:', err)
        } finally {
            setRewardsModalLoading(false)
        }
    }

    // 提现相关API函数
    // 获取提现记录
    const fetchWithdrawalRecords = async () => {
        try {
            setWithdrawalLoading(true)
            const response = await api.get('/api/withdrawal/history?limit=20&offset=0')
            setWithdrawalRecords(response.data.data || [])
        } catch (err) {
            console.error('Failed to fetch withdrawal records:', err)
        } finally {
            setWithdrawalLoading(false)
        }
    }

    // 提交提现申请
    const submitWithdrawalRequest = async () => {
        try {
            setWithdrawalLoading(true)
            setWithdrawalError('')
            setWithdrawalSuccess('')

            // Form validation
            if (withdrawalForm.amount <= 0) {
                setWithdrawalError(t('withdrawalAmountMustBeGreaterThanZero'))
                return
            }

            if (withdrawalForm.amount > availableBalance) {
                setWithdrawalError(t('withdrawalAmountCannotExceedBalance'))
                return
            }

            if (!withdrawalForm.paypal_email.trim()) {
                setWithdrawalError(t('enterPaypalEmailAddress'))
                return
            }

            if (!withdrawalForm.password.trim()) {
                setWithdrawalError(t('enterPasswordForVerification'))
                return
            }

            const response = await api.post('/api/withdrawal/request', withdrawalForm)
            
            if (response.data.success) {
                setShowWithdrawalModal(false)
                setShowSuccessModal(true);
                // Refresh referral info and withdrawal records
                fetchReferralInfo()
                fetchAvailableBalance()
                fetchWithdrawalRecords()
                // Reset form
                setWithdrawalForm({
                    amount: 0,
                    password: '',
                    withdrawal_method: 'paypal',
                    paypal_email: ''
                })
            }
        } catch (err: any) {
            setWithdrawalError(err.response?.data?.message || t('withdrawalRequestFailed'))
        } finally {
            setWithdrawalLoading(false)
        }
    }

    // 提交转换申请
    const submitConvertRequest = async () => {
        try {
            setConvertLoading(true)
            setConvertError('')
            setConvertSuccess('')

            const response = await api.post('/api/balance/transfer', {
                amount: convertAmount
            })
            
            if (response.data.success) {
                // 立即更新Available Balance状态，避免等待API调用
                const newAvailableBalance = availableBalance - convertAmount
                setAvailableBalance(Math.max(0, newAvailableBalance))
                
                // 显示成功弹窗
                setShowConvertSuccessModal(true)
                setShowConvertModal(false)
                
                // 刷新其他相关数据
                fetchReferralInfo()
                fetchRewardBalance()
                fetchAvailableBalance() // 保证数据同步
                
                // Reset form
                setConvertAmount(0)
            }
        } catch (err: any) {
            // 处理不同类型的错误并提供准确的错误信息
            let errorMessage = t('conversionFailed')
            
            if (err.response) {
                // 服务器返回的错误
                const status = err.response.status
                const data = err.response.data
                
                if (status === 400) {
                    // 客户端错误
                    if (data?.error?.includes('insufficient') || data?.message?.includes('insufficient')) {
                        errorMessage = t('insufficientRewardBalance')
                    } else if (data?.error?.includes('minimum') || data?.message?.includes('minimum')) {
                        errorMessage = data.message || data.error || t('conversionAmountMinimumRequirement')
                    } else if (data?.error?.includes('maximum') || data?.message?.includes('maximum')) {
                        errorMessage = data.message || data.error || t('conversionAmountMaximumLimit')
                    } else {
                        errorMessage = data?.message || data?.error || t('invalidConversionRequest')
                    }
                } else if (status === 401) {
                    errorMessage = t('authenticationFailed')
                } else if (status === 403) {
                    errorMessage = t('conversionNotAllowed')
                } else if (status === 429) {
                    errorMessage = t('tooManyRequests')
                } else if (status >= 500) {
                    errorMessage = t('serverError')
                } else {
                    errorMessage = data?.message || data?.error || t('requestFailed', { status })
                }
            } else if (err.request) {
                // 网络错误
                errorMessage = t('networkError')
            } else {
                // 其他错误
                errorMessage = err.message || t('unexpectedError')
            }
            
            setConvertError(errorMessage)
        } finally {
            setConvertLoading(false)
        }
    }

    // 显示转换确认弹窗
    const handleConvertClick = () => {
        if (convertAmount <= 0) {
            setConvertError(t('conversionAmountMustBeGreaterThanZero'))
            return
        }

        if (availableBalance <= 0) {
            setConvertError(t('insufficientBalanceCurrentZero'))
            return
        }

        if (convertAmount > availableBalance) {
            setConvertError(t('conversionAmountCannotExceedAvailableBalance', { balance: availableBalance.toFixed(2) }))
            return
        }

        // 清除错误信息并显示确认弹窗
        setConvertError('')
        setShowConvertConfirmModal(true)
    }

    // 获取提现手续费预览
    const fetchWithdrawalFeePreview = async (amount: number) => {
        try {
            const response = await api.get(`/api/withdrawal/fee-preview?amount=${amount}`)
            return response.data
        } catch (err) {
            console.error('Failed to fetch withdrawal fee preview:', err)
            // 如果API调用失败，使用本地计算作为备用
            return calculateWithdrawalFee(amount)
        }
    }

    // 计算提现手续费（备用方法）
    const calculateWithdrawalFee = (amount: number) => {
        // 假设手续费为2%，最低1元
        const feeRate = 0.02
        const fee = Math.max(amount * feeRate, 1)
        return {
            fee: fee,
            actualAmount: amount - fee
        }
    }

    // 计算奖励统计数据
    const calculateRewardStats = () => {
        if (!rewardHistory || rewardHistory.length === 0) {
            return {
                thisMonthRewards: 0,
                last30DaysEarned: 0,
                latestRewardTime: null,
                totalEarned: referralInfo?.total_earnings || 0
            }
        }

        const now = new Date()
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        const last30Days = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000))

        const thisMonthRewards = rewardHistory.filter(reward => 
            new Date(reward.created_at) >= thisMonthStart
        ).length

        const last30DaysEarned = rewardHistory
            .filter(reward => new Date(reward.created_at) >= last30Days)
            .reduce((sum, reward) => sum + parseFloat(reward.reward_amount.toString()), 0)

        const latestRewardTime = rewardHistory.length > 0 ? rewardHistory[0].created_at : null

        return {
            thisMonthRewards,
            last30DaysEarned: last30DaysEarned.toFixed(2),
            latestRewardTime,
            totalEarned: referralInfo?.total_earnings || 0
        }
    }

    const rewardStats = calculateRewardStats()


    useEffect(() => {
        const loadData = async () => {
            setLoading(true)
            await Promise.all([
                fetchReferralInfo(),
                fetchRewardHistory(),
                fetchRewardBalance(),
                fetchAvailableBalance(),
            ])
            setLoading(false)
        }
        loadData()
    }, [])

    // 根据首字母获取头像背景颜色
    const getAvatarColor = (letter: string) => {
        const colors: { [key: string]: string } = {
            'A': 'bg-red-500', 'B': 'bg-orange-500', 'C': 'bg-amber-500', 'D': 'bg-yellow-600',
            'E': 'bg-lime-600', 'F': 'bg-green-500', 'G': 'bg-emerald-500', 'H': 'bg-teal-500',
            'I': 'bg-cyan-500', 'J': 'bg-sky-500', 'K': 'bg-blue-500', 'L': 'bg-indigo-500',
            'M': 'bg-violet-500', 'N': 'bg-purple-500', 'O': 'bg-fuchsia-500', 'P': 'bg-pink-500',
            'Q': 'bg-rose-500', 'R': 'bg-red-600', 'S': 'bg-orange-600', 'T': 'bg-amber-600',
            'U': 'bg-green-600', 'V': 'bg-teal-600', 'W': 'bg-blue-600', 'X': 'bg-indigo-600',
            'Y': 'bg-purple-600', 'Z': 'bg-pink-600'
        };
        return colors[letter.toUpperCase()] || 'bg-gray-500';
    };

    // 复制推荐链接
    const copyReferralLink = async () => {
        if (!referralInfo?.referral_link) return

        try {
            await navigator.clipboard.writeText(referralInfo.referral_link)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
      
        }
    }

    // 独立页面头部组件
    const renderHeader = () => (
        <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center space-x-8">
                        {/* Logo部分 */}
                        <a 
                            href="https://www.modelpool.net/#" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
                            title="Visit ModelPool Official Website"
                        >
                            <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="h-6 w-6" />
                            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                ModelPool
                            </h1>
                        </a>
                        
                        {/* 页面标题 */}
                        <div className="flex items-center space-x-2">
                            <span className="text-gray-400">/</span>
                            <span className="text-lg font-semibold text-gray-900">{t('referrals_title')}</span>
                        </div>
                    </div>
                    
                    {/* 右侧操作按钮 */}
                    <div className="flex justify-end items-center">
                        {/* My Workspace 按钮 */}
                        <button
                            onClick={() => window.location.href = '/dashboard/models'}
                            className="flex items-center gap-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
                        >
                            <span className="font-medium text-gray-700">{t('myWorkspace')}</span>
                            <div className={`w-7 h-7 rounded flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(localStorage.getItem('username')?.charAt(0) || 'U')}`}>
                                {localStorage.getItem('username')?.charAt(0).toUpperCase() || 'U'}
                            </div>
                        </button>
                        <LanguageSelector />
                    </div>
                </div>
            </div>
        </nav>
    );

    if (loading) {
        return (
            <>
                {renderHeader()}
                <LoadingSpinner size="medium" text={t('loadingReferralData')} fullPage={true} />
            </>
        )
    }

    if (error) {
        return (
            <>
                {renderHeader()}
                <div className="bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="text-center py-12">
                            <p className="text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            {renderHeader()}
            <div className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 space-y-4">

                    {/* 第一排：用户等级卡片和Statistics Overview卡片 */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* 用户等级卡片 - 占1/3宽度 */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg p-3 shadow-sm h-full flex flex-col">
                                {/* 上部内容区域 */}
                                <div className="flex-1">
                                    {/* 第一行：头像和等级信息 */}
                                    <div className="flex items-center gap-3 mb-4">
                                        {/* 用户头像 - 增大尺寸 */}
                                        <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold ${getAvatarColor(localStorage.getItem('username')?.charAt(0) || 'U')}`}>
                                            {localStorage.getItem('username')?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        
                                        {/* 等级信息 */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                            <Crown className="h-5 w-5 text-yellow-500" />
                            <h3 className="text-lg font-bold text-gray-900 truncate">
                                {referralInfo?.level_info?.level_name || t('bronzeI')}
                            </h3>
                            <span className="text-sm text-gray-500">
                                (Lv.{referralInfo?.user_level || 1})
                            </span>
                        </div>
                        <p className="text-sm text-gray-600">
                            {t('commission')}: <span className="font-bold text-green-600">{referralInfo?.level_info?.reward_percentage || 10}%</span>
                        </p>
                                        </div>
                                        
                                        {/* 等级进度 */}
                                        <div className="text-right min-w-0 flex-shrink-0">
                                            <p className="text-sm text-gray-600 mb-2">{t('nextLevel')}</p>
                                            <div className="w-24 bg-gray-200 rounded-full h-2 mb-2">
                                                <div 
                                                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${referralInfo?.level_info?.progress_to_next || 0}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {referralInfo?.level_info?.progress_to_next || 0}%
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* 第二行：余额显示和操作按钮 */}
                                    <div className="flex items-end justify-between gap-4">
                                        {/* 余额显示 - 加强视觉冲击 */}
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-600 mb-2">{t('availableBalance')}</p>
                                            <p className="text-3xl font-bold text-blue-600">
                                                ${availableBalance.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                        
                                        {/* 操作按钮组 */}
                                        <div className="flex-shrink-0 flex gap-2">
                                            {/* Convert to Balance 按钮 */}
                                            <button
                                                onClick={() => setShowConvertModal(true)}
                                                disabled={!referralInfo?.total_earnings || referralInfo.total_earnings <= 0}
                                                className="bg-gray-900 text-white py-2 px-4 rounded text-sm hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                                            >
                                                <ArrowRightLeft className="h-4 w-4" />
                                                {t('convert')}
                                            </button>
                                            
                                            {/* Withdraw 按钮 */}
                                            <button
                                                onClick={() => setShowWithdrawalModal(true)}
                                                disabled={availableBalance <= 0}
                                                className="bg-gray-900 text-white py-2 px-4 rounded text-sm hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                                            >
                                                {t('withdraw')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Statistics Overview卡片 - 占2/3宽度 */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-lg p-3 shadow-sm h-full flex flex-col">
                                <h2 className="text-sm font-bold text-green-600 mb-2">{t('statisticsOverview')}</h2>
                                
                                {/* 统计数据网格 - 更紧凑的布局 */}
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {/* Total Referrals */}
                                    <div className="bg-gray-50 rounded p-2 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs font-medium text-gray-600 mb-1">{t('totalReferrals')}</p>
                                        <p className="text-xl font-bold text-gray-900">{referralInfo?.referral_count || 0}</p>
                                    </div>
                                    
                                    {/* Recharge Rewards */}
                                    <div className="bg-gray-50 rounded p-2 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs font-medium text-gray-600 mb-1">{t('recharge')}</p>
                                        <p className="text-xl font-bold text-gray-900">{referralInfo?.recharge_rewards || 0}</p>
                                    </div>
                                    
                                    {/* Total Earnings */}
                                    <div className="bg-gray-50 rounded p-2 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs font-medium text-gray-600 mb-1">{t('totalEarnings')}</p>
                                        <p className="text-xl font-bold text-gray-900">${referralInfo?.total_earnings || 0}</p>
                                    </div>
                                </div>

                                {/* 快捷操作按钮 - 更紧凑的布局 */}
                                <div className="mt-auto flex justify-end">
                                    {/* Records 按钮 - 右对齐并减小宽度 */}
                                    <button
                                        onClick={() => {
                                            setShowWithdrawalRecordsModal(true)
                                            fetchWithdrawalRecords()
                                        }}
                                        className="bg-gray-900 text-white py-1.5 px-3 rounded text-xs hover:bg-gray-800 transition-colors font-medium"
                                    >
                                        {t('withdrawalRecords')}
                                    </button>
                                </div>

                                {/* 成功提示消息 - 只显示提现成功 */}
                                {withdrawalSuccess && (
                                    <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                                        <p className="text-xs text-green-700">{withdrawalSuccess}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 第二排：Your Referral Link、Recent Referrals和 Recent Rewards 三个卡片并排 */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Your Referral Link卡片 */}
                        <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col h-full">
                            <h2 className="text-sm font-bold text-gray-900 mb-3">{t('yourReferralLink')}</h2>

                            <div className="bg-gray-50 rounded p-3 flex-1 flex flex-col justify-center">
                                 <div className="mb-4">
                                     <p className="text-xs text-gray-600 mb-2">{t('referralCode')}:</p>
                                     <p className="text-lg font-mono font-bold text-indigo-600 break-all">
                                         {referralInfo?.referral_code || t('loading')}
                                     </p>
                                 </div>
                                 <div>
                                     <p className="text-xs text-gray-600 mb-2">{t('referralLink')}:</p>
                                     <div className="flex gap-2">
                                         <input
                                             type="text"
                                             value={referralInfo?.referral_link || ''}
                                             readOnly
                                             className="flex-1 text-xs bg-white border border-gray-300 rounded px-2 py-1"
                                         />
                                         <button
                                             onClick={copyReferralLink}
                                             className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                                 copied
                                                     ? 'bg-green-100 text-green-700 border border-green-300'
                                                     : 'bg-gray-900 text-white hover:bg-gray-800'
                                                 }`}
                                         >
                                             <Copy className="h-3 w-3 inline mr-1" />
                                             <span>{copied ? t('copied') : t('copy')}</span>
                                         </button>
                                     </div>
                                 </div>
                             </div>
                        </div>

                        {/* Recent Referrals卡片 */}
                        <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col">
                            <h3 className="text-sm font-bold text-gray-900 mb-2">{t('recentReferrals')}</h3>

                            <div className="space-y-2 flex-1 pb-2">
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('thisMonth')}:</span>
                                    <span className="text-xs font-medium text-gray-900">{referralInfo?.referral_stats?.this_month_count || 0} {t('new')}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('last30Days')}:</span>
                                    <span className="text-xs font-medium text-gray-900">{referralInfo?.referral_stats?.last_30_days_count || 0} {t('total')}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('latest')}:</span>
                                    <span className="text-xs font-medium text-gray-900">
                                        {referralInfo?.referral_stats?.latest_referral_time
                                            ? new Date(referralInfo.referral_stats.latest_referral_time).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric'
                                            })
                                            : t('none')
                                        }
                                    </span>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-gray-200 mt-auto">
                                <div className="flex justify-center">
                                     <button
                                          onClick={() => {
                                              setShowUsersModal(true)
                                              fetchReferredUsersForModal(1)
                                          }}
                                         className="bg-gray-900 text-white py-1.5 px-3 rounded text-xs hover:bg-gray-800 transition-colors font-medium"
                                     >
                                         {t('viewAll')}
                                     </button>
                                 </div>
                            </div>
                        </div>

                        {/* Recent Rewards */}
                        <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col">
                            <h3 className="text-sm font-bold text-gray-900 mb-2">{t('recentRewards')}</h3>
                            <div className="space-y-2 flex-1 pb-2">
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('totalEarnings')}:</span>
                                    <span className="text-xs font-medium text-gray-900">${Number(rewardStats.totalEarned).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('last30Days')}:</span>
                                    <span className="text-xs font-medium text-gray-900">${Number(rewardStats.last30DaysEarned).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('thisMonth')}:</span>
                                    <span className="text-xs font-medium text-gray-900">{rewardStats.thisMonthRewards} {t('rewards')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-600">{t('latestReward')}:</span>
                                    <span className="text-xs font-medium text-gray-900">
                                        {rewardStats.latestRewardTime
                                            ? new Date(rewardStats.latestRewardTime).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric'
                                            })
                                            : t('none')
                                        }
                                    </span>
                                </div>
                            </div>
                            <div className="pt-2 border-t border-gray-200 mt-auto">
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => {
                                            setShowRewardsModal(true)
                                            fetchRewardsForModal(1)
                                        }}
                                        className="bg-gray-900 text-white py-1.5 px-3 rounded text-xs hover:bg-gray-800 transition-colors font-medium"
                                    >
                                        {t('viewAll')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 推荐限制提示 */}
                    {referralInfo && referralInfo.referral_count >= (referralInfo.config?.max_referrals || 100) * 0.8 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-700">
                                <strong>{t('notice')}:</strong> {t('referralLimitWarning', { 
                                    current: referralInfo.referral_count, 
                                    max: referralInfo.config?.max_referrals || 100 
                                })}
                                {referralInfo.referral_count >= (referralInfo.config?.max_referrals || 100) &&
                                    ` ${t('referralLimitReached')}`
                                }
                            </p>
                        </div>
                    )}

                    {/* 等级系统展示 */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="mb-4">
                            <div className="flex items-center mb-3">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gray-800 rounded-lg blur-sm opacity-30"></div>
                                    <div className="relative bg-gray-800 text-white px-6 py-2 rounded-lg shadow-lg border border-gray-700">
                                        <div className="flex items-center gap-2">
                                            <Crown className="h-5 w-5 text-yellow-400" />
                                            <h2 className="text-lg font-bold tracking-wide">TIERS</h2>
                                            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <p className="text-sm text-blue-700 flex items-center">
                                    <span className="w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs mr-2">i</span>
                                    {t('tierSystemDescription')}
                                </p>
                            </div>
                        </div>

                        {/* 等级网格 */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Bronze I - Level 1 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('bronzeI')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarnings', { percentage: 10 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsRequirement', { amount: 0 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bronze II - Level 2 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('bronzeII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 12 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 60 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bronze III - Level 3 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('bronzeIII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 14 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 200 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Silver I - Level 4 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('silverI')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 16 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 500 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Silver II - Level 5 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('silverII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 18 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 1000 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Silver III - Level 6 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('silverIII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 20 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 2500 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Gold I - Level 7 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('goldI')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 22 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 5000 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Gold II - Level 8 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('goldII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 24 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 10000 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Gold III - Level 9 */}
                            <div className="bg-gray-800 rounded-lg p-4 text-white relative overflow-hidden">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                                            <Crown className="h-5 w-5 text-white" />
                                        </div>
                                        <span className="font-bold">{t('goldIII')}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-400">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        <span className="font-bold">{t('ofEarningsPercent', { percentage: 26 })}</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">{t('requirements')}</h4>
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                        <span>{t('referralEarningsAmount', { amount: 25000 })}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 第10级 - 特殊大卡片 */}
                        <div className="mt-4">
                            <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 rounded-lg p-6 text-white relative overflow-hidden border-2 border-yellow-400">
                                {/* 背景装饰 */}
                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 via-transparent to-purple-600/10"></div>
                                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/5 rounded-full -translate-y-16 translate-x-16"></div>
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-600/5 rounded-full translate-y-12 -translate-x-12"></div>
                                
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-4">
                                                <div className="relative">
                                                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                                                    {/* 更像宝石的钻石形状内联SVG */}
                                                    <svg
                                                        className="h-10 w-10 text-white drop-shadow-lg"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        aria-hidden="true"
                                                    >
                                                        {/* 外轮廓 */}
                                                        <path d="M12 2 L19 8 L12 22 L5 8 Z" />
                                                        {/* 上边横线 */}
                                                        <path d="M5 8 L19 8" />
                                                        {/* 顶部切面斜线 */}
                                                        <path d="M8 8 L12 2" />
                                                        <path d="M16 8 L12 2" />
                                                        {/* 底部切面斜线 */}
                                                        <path d="M8 8 L12 22" />
                                                        <path d="M16 8 L12 22" />
                                                    </svg>
                                                    </div>
                                                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                                                        <span className="text-xs font-bold text-black">10</span>
                                                    </div>
                                                </div>
                                            <div>
                                                <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 via-yellow-300 to-amber-400 bg-clip-text mb-1">
                                                    {t('diamondEliteMaster')}
                                                </h3>
                                                <p className="text-gray-300 text-sm">The Ultimate Affiliate Level</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-2 text-yellow-400 mb-1">
                                                <ArrowRightLeft className="h-6 w-6" />
                                                <span className="text-3xl font-bold">30%</span>
                                            </div>
                                            <p className="text-sm text-gray-300">{t('ofEarnings')}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* 要求列表 */}
                                        <div>
                                            <h4 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
                                                <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                                                Requirements
                                            </h4>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 border-2 border-yellow-400 rounded-full flex items-center justify-center">
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                                    </div>
                                                    <span className="text-gray-200">$50000 total referral earnings</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* 特权列表 */}
                                        <div>
                                            <h4 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
                                                <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                                                {t('eliteBenefits')}
                                            </h4>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                                                        <span className="text-black text-xs font-bold">✓</span>
                                                    </div>
                                                    <span className="text-gray-200">{t('priorityCustomerSupport')}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                                                        <span className="text-black text-xs font-bold">✓</span>
                                                    </div>
                                                    <span className="text-gray-200">{t('exclusiveEarlyAccess')}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                                                        <span className="text-black text-xs font-bold">✓</span>
                                                    </div>
                                                    <span className="text-gray-200">{t('dedicatedAccountManager')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* 底部装饰 */}
                                    <div className="mt-6 pt-4 border-t border-yellow-400/30">
                                        <div className="text-center">
                                            <p className="text-yellow-400 font-medium text-sm">
                                {t('congratulationsEliteMaster')}
                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>



                </div>
            </div>
            {/* 推荐用户列表弹窗 */}
            {showUsersModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-base font-semibold text-gray-900">{t('allReferredUsers')}</h3>
                                <button
                                    onClick={() => setShowUsersModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[70vh]">
                            {modalLoading && modalUsers.length === 0 ? (
                            <div className="text-center py-8">
                                <LoadingSpinner size="medium" text={t('loadingUsers')} />
                            </div>
                        ) : modalUsers.length > 0 ? (
                                <div className="space-y-2">
                                    {modalUsers.map((user) => (
                                        <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                                <p className="text-xs text-gray-600">
                                                    {t('joined')}: {new Date(user.registered_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    user.payment_count > 0 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {user.payment_count > 0 ? t('paid') : t('unpaid')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    {modalTotalPages > 1 && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <Pagination
                                                currentPage={modalPage}
                                                totalPages={modalTotalPages}
                                                totalRecords={modalTotalRecords}
                                                pageSize={modalPageSize}
                                                onPageChange={(page) => fetchReferredUsersForModal(page, modalPageSize)}
                                                onPageSizeChange={(size) => {
                                                    setModalPageSize(size)
                                                    fetchReferredUsersForModal(1, size)
                                                }}
                                                loading={modalLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">{t('noReferralsYet')}</p>
                                    <p className="text-sm text-gray-500">{t('startSharingToSeeReferrals')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* 奖励历史弹窗 */}
            {showRewardsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-base font-semibold text-gray-900">{t('allRewardHistory')}</h3>
                                <button
                                    onClick={() => setShowRewardsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[70vh]">
                            {rewardsModalLoading && modalRewards.length === 0 ? (
                            <div className="text-center py-8">
                                <LoadingSpinner size="medium" text={t('loadingRewards')} />
                            </div>
                        ) : modalRewards.length > 0 ? (
                                <div className="space-y-2">
                                    {modalRewards.map((reward) => (
                                        <div key={reward.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                            <div>
                                                <div className="flex items-center">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${reward.reward_type === 'registration'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-green-100 text-green-800'
                                                        }`}>
                                                        {reward.reward_type === 'registration' ? t('registration') : t('recharge')}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                    {t('from')}: {reward.referred_email}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(reward.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-green-600">+${reward.reward_amount}</p>
                                                {reward.recharge_amount > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('fromPayment', { amount: reward.recharge_amount })}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {rewardsModalTotalPages > 1 && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <Pagination
                                                currentPage={rewardsModalPage}
                                                totalPages={rewardsModalTotalPages}
                                                totalRecords={rewardsModalTotalRecords}
                                                pageSize={rewardsModalPageSize}
                                                onPageChange={(page) => fetchRewardsForModal(page, rewardsModalPageSize)}
                                                onPageSizeChange={(size) => {
                                                    setRewardsModalPageSize(size)
                                                    fetchRewardsForModal(1, size)
                                                }}
                                                loading={rewardsModalLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">{t('noRewardsYet')}</p>
                                    <p className="text-sm text-gray-500">{t('rewardsWillAppearHere')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 转换弹窗 */}
            {showConvertModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-900">{t('convertToBalance')}</h3>
                                <button
                                    onClick={() => {
                                        setShowConvertModal(false)
                                        setConvertError('')
                                        setConvertSuccess('')
                                    }}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="space-y-4">
                                {/* 可用余额显示 */}
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">{t('availableReferralRewards')}</span>
                                        <span className={`text-lg font-bold ${
                                            availableBalance > 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            ${availableBalance.toFixed(2)}
                                        </span>
                                    </div>
                                    {availableBalance <= 0 && (
                                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                            <p className="text-xs text-red-600">
                                                {t('insufficientBalanceAlert')}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* 转换金额 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('convertAmountDollar')}
                                    </label>
                                    <input
                                        type="number"
                                        min="0.01"
                                        max={availableBalance}
                                        step="0.01"
                                        value={convertAmount || ''}
                                        onChange={(e) => setConvertAmount(parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder={t('enterAmountToConvert')}
                                    />
                                </div>

                                {/* 转换说明 */}
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <div className="flex items-start">
                                        <ArrowRightLeft className="h-5 w-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                                        <div className="text-sm text-purple-700">
                                            <p className="font-medium mb-1">{t('convertRewardsTitle')}</p>
                                            <p>{t('convertRewardsDescription')}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* 转换预览 */}
                                {convertAmount > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('convertAmountLabel')}</span>
                                                <span className="font-medium">${convertAmount.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('processingFee')}</span>
                                                <span className="font-medium text-green-600">{t('free')}</span>
                                            </div>
                                            <div className="flex justify-between border-t border-blue-200 pt-1">
                                                <span className="text-gray-900 font-medium">{t('youWillReceive')}</span>
                                                <span className="font-bold text-green-600">${convertAmount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 错误信息 */}
                                {convertError && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">{convertError}</p>
                                    </div>
                                )}

                                {/* 提交按钮 */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setShowConvertModal(false)
                                            setConvertError('')
                                            setConvertSuccess('')
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        onClick={handleConvertClick}
                                        disabled={convertLoading || convertAmount <= 0 || availableBalance <= 0}
                                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        title={availableBalance <= 0 ? t('insufficientBalanceForConversion') : ''}
                                    >
                                        {convertLoading ? t('converting') : 
                                         availableBalance <= 0 ? t('insufficientBalance') : t('convertNow')
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 提现申请弹窗 */}
            {showWithdrawalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-900">{t('withdrawFunds')}</h3>
                                <button
                                    onClick={() => {
                                        setShowWithdrawalModal(false)
                                        setWithdrawalError('')
                                        setWithdrawalSuccess('')
                                    }}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="space-y-4">
                                {/* 可用余额显示 */}
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">{t('availableBalanceLabel')}</span>
                                        <span className="text-lg font-bold text-green-600">${availableBalance.toFixed(2) || '0.00'}</span>
                                    </div>
                                </div>

                                {/* 提现金额 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('withdrawalAmountDollar')}
                                    </label>
                                    <input
                                        type="number"
                                        min="10"
                                        max={availableBalance || 0}
                                        step="0.01"
                                        value={withdrawalForm.amount || ''}
                                        onChange={(e) => setWithdrawalForm({
                                            ...withdrawalForm,
                                            amount: parseFloat(e.target.value) || 0
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-xs"
                                        placeholder={t('enterAmountMinimum')}
                                    />
                                </div>

                                {/* 提现方式 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('paymentMethod')}
                                    </label>
                                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                                        <div className="flex items-center">
                                            <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="none">
                                                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-.635 4.106z" fill="#0070ba"/>
                                                <path d="m12.83 6.232c-.076.695-.27 1.24-.58 1.634-.31.394-.738.591-1.284.591H9.614c-.524 0-.968.382-1.05.9l-.635 4.106c-.058.375.203.698.58.698h2.19c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437.292-1.867-.002-3.137-1.012-4.287C17.29.543 15.282 0 12.712 0H5.252c-.524 0-.972.382-1.054.9L1.091 20.597a.641.641 0 0 0 .633.74h4.606l1.35-8.105z" fill="#003087"/>
                                            </svg>
                                            <span className="font-medium text-gray-900">PayPal</span>
                                        </div>
                                        <span className="ml-auto text-sm text-gray-500">{t('onlySupportedMethod')}</span>
                                    </div>
                                </div>

                                {/* PayPal账户信息 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('paypalEmailAddress')}
                                    </label>
                                    <input
                                        type="email"
                                        value={withdrawalForm.paypal_email}
                                        onChange={(e) => setWithdrawalForm({
                                            ...withdrawalForm,
                                            paypal_email: e.target.value
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-xs"
                                        placeholder={t('enterPaypalEmailAddress')}
                                    />
                                </div>

                                {/* 密码验证 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('passwordVerification')}
                                    </label>
                                    <input
                                        type="password"
                                        value={withdrawalForm.password}
                                        onChange={(e) => setWithdrawalForm({
                                            ...withdrawalForm,
                                            password: e.target.value
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-xs"
                                        placeholder={t('enterPasswordForVerification')}
                                    />
                                </div>

                                {/* 手续费预览 */}
                                {withdrawalForm.amount > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('withdrawalAmountLabel')}</span>
                                                <span className="font-medium">${withdrawalForm.amount}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">{t('processingFee')} (2%)</span>
                                                <span className="font-medium">-${calculateWithdrawalFee(withdrawalForm.amount).fee.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between border-t border-blue-200 pt-1">
                                                <span className="text-gray-900 font-medium">{t('youWillReceive')}</span>
                                                <span className="font-bold text-green-600">${calculateWithdrawalFee(withdrawalForm.amount).actualAmount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 错误信息 */}
                                {withdrawalError && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">{withdrawalError}</p>
                                    </div>
                                )}

                                {/* 提交按钮 */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setShowWithdrawalModal(false)
                                            setWithdrawalError('')
                                            setWithdrawalSuccess('')
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        onClick={submitWithdrawalRequest}
                                        disabled={withdrawalLoading || withdrawalForm.amount <= 0 || !withdrawalForm.paypal_email.trim() || !withdrawalForm.password.trim()}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                        {withdrawalLoading ? t('submitting') : t('submitRequest')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 提现记录弹窗 */}
            {showWithdrawalRecordsModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-900">{t('withdrawalRecords')}</h3>
                                <button
                                    onClick={() => setShowWithdrawalRecordsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[70vh]">
                            {withdrawalLoading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="medium" text={t('loadingRecords')} />
                                </div>
                            ) : withdrawalRecords.length > 0 ? (
                                <div className="space-y-3">
                                    {withdrawalRecords.map((record) => (
                                        <div key={record.id} className="bg-gray-50 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-gray-900">${record.amount}</span>
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            record.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                            record.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                                            record.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {record.status === 'pending' ? t('pending') :
                                                             record.status === 'processing' ? t('processing') :
                                                             record.status === 'completed' ? t('completed') :
                                                             t('rejected')}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-600">
                                                        {t('method')}: PayPal
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        {t('account')}: {record.accountInfo}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {t('applied')}: {new Date(record.createdAt).toLocaleString()}
                                                    </p>
                                                    {record.processedAt && (
                                                        <p className="text-xs text-gray-500">
                                                            {t('processed')}: {new Date(record.processedAt).toLocaleString()}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm text-gray-600">{t('fee')}: ${record.fee}</p>
                                                    <p className="font-medium text-green-600">{t('receivedAmount')} ${record.actualAmount}</p>
                                                </div>
                                            </div>
                                            
                                            {record.note && (
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <p className="text-xs text-gray-600">{t('note')}: {record.note}</p>
                                                </div>
                                            )}
                                            
                                            {record.status === 'rejected' && record.rejectionReason && (
                                                <div className="mt-2 pt-2 border-t border-red-200 bg-red-50 rounded p-2">
                                                    <p className="text-xs text-red-700">
                                                        <strong>{t('rejectionReason')}</strong> {record.rejectionReason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">{t('noWithdrawalRecords')}</p>
                                    <p className="text-sm text-gray-500">{t('withdrawalHistoryAppears')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 余额转换确认弹窗 */}
            <ConfirmDialog
                isOpen={showConvertConfirmModal}
                type="warning"
                title={t('confirmBalanceConversion')}
                message={t('confirmBalanceConversion_message', { amount: convertAmount })}
                confirmText={t('convertNow')}
                cancelText={t('cancel')}
                onConfirm={() => {
                    setShowConvertConfirmModal(false)
                    submitConvertRequest()
                }}
                onCancel={() => setShowConvertConfirmModal(false)}
            />

            {/* 余额转换成功弹窗 */}
            <ConfirmDialog
                isOpen={showConvertSuccessModal}
                type="info"
                title={t('conversionSuccessTitle')}
                message={t('conversionSuccessMessage', { amount: convertAmount })}
                confirmText={t('great')}
                onConfirm={() => setShowConvertSuccessModal(false)}
                onCancel={() => setShowConvertSuccessModal(false)}
            />

            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title={t('withdrawalRequestSubmitted')}
                message={t('withdrawalSubmittedMessage')}
            />
        </>
    )
}

export default Referrals